import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

export function useLabourRate(contractorId, bagTypeId) {
  return useQuery({
    queryKey: ["labour-rate", contractorId, bagTypeId],
    queryFn: async () =>
      (await api.get("/masters/labour-agreements/rate", {
        params: { contractor_id: contractorId, bag_type_id: bagTypeId },
      })).data,
    enabled: !!contractorId && !!bagTypeId,
  });
}

function useEntity(entity) {
  return useQuery({
    queryKey: ["masters", entity],
    queryFn: async () => (await api.get(`/masters/${entity}`)).data,
  });
}

export function useMasters() {
  const parties = useEntity("parties");
  const warehouses = useEntity("warehouses");
  const stacks = useEntity("stacks");
  const items = useEntity("stock_items");
  const bagTypes = useEntity("bag_types");
  const vehicles = useEntity("vehicles");
  const chargeTypes = useEntity("charge_types");
  return {
    parties: parties.data || [],
    warehouses: warehouses.data || [],
    stacks: stacks.data || [],
    items: items.data || [],
    bagTypes: bagTypes.data || [],
    vehicles: vehicles.data || [],
    chargeTypes: chargeTypes.data || [],
    loading: [parties, warehouses, stacks, items, bagTypes, vehicles].some((q) => q.isLoading),
  };
}

export const opt = (arr, labelFn, valueKey = "id") =>
  (arr || []).filter((x) => x.active !== false).map((x) => ({ value: x[valueKey], label: labelFn(x) }));

/** Include inactive masters (rent must bill historical stock on deactivated WH/stacks). */
export const optIncludingInactive = (arr, labelFn, valueKey = "id") =>
  (arr || []).map((x) => ({
    value: x[valueKey],
    label: x.active === false ? `${labelFn(x)} (Inactive)` : labelFn(x),
  }));

export function partiesByRole(parties, role) {
  return (parties || []).filter(
    (p) => p.active !== false && Array.isArray(p.roles) && p.roles.includes(role)
  );
}

/** Parties that have at least one of the given roles. */
export function partiesByRoles(parties, roles) {
  const allowed = new Set(roles);
  return (parties || []).filter(
    (p) => p.active !== false && Array.isArray(p.roles) && p.roles.some((r) => allowed.has(r))
  );
}

export function vehicleOptions(vehicles) {
  return (vehicles || [])
    .filter((v) => v.active !== false)
    .map((v) => ({ value: v.id, label: v.vehicle_number, hint: v.driver || v.vehicle_type || "", raw: v }));
}

export function matchVehicle(vehicles, id, number) {
  if (id) {
    const byId = (vehicles || []).find((v) => String(v.id) === String(id));
    if (byId) return byId;
  }
  const q = String(number || "").trim().toLowerCase();
  if (!q) return null;
  return (vehicles || []).find((v) => String(v.vehicle_number || "").toLowerCase() === q) || null;
}
